/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/useScopeAgContextMenu.ts
 * FILE_ID: QB-AGGRID-CTXMENU-HOOK-001
 * PURPOSE: Own the context-menu state for a single section's grid:
 *          (open?, click coords, target rowId). Provides four pure
 *          action callbacks (Insert above / below / Duplicate /
 *          Delete) that close the menu and route through the existing
 *          onAddItem / onDeleteItem persistence contract.
 * WHY: Encapsulates the SAM-style right-click affordance so
 *      ScopeAgGridSection can stay focused on grid orchestration. The
 *      logic for "insert at this sort_order" is non-trivial and
 *      tested in isolation.
 * FLOW:
 *   ┌───────────────────┐   ┌────────────────────┐   ┌──────────────────────┐
 *   │ onCellContextMenu │ → │ openAt(rowId,x,y)  │ → │ menu rendered        │
 *   └───────────────────┘   └────────────────────┘   └──────────────────────┘
 *   ┌────────────────────┐   ┌─────────────────────┐   ┌────────────────────┐
 *   │ user clicks Insert │ → │ build payload with  │ → │ onAddItem / onDelete│
 *   │ above (etc.)       │   │ neighbour sort_order│   │                    │
 *   └────────────────────┘   └─────────────────────┘   └────────────────────┘
 * RELATED:
 *   - QB-AGGRID-CTXMENU-001 (ScopeAgGridContextMenu.tsx) — popup
 *   - QB-AGGRID-PAD-ROWS-001 — buildPromotePayload base case
 */

import { useCallback, useMemo, useState } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import { isPadRowId } from './pad-row-helpers'

export interface UseScopeAgContextMenuArgs {
  sectionId: string
  rows: QuoteLineItem[]
  isEditable: boolean
  configDefaults: {
    defaultProductivityPmpd?: number | string | null
    defaultUnit?: string
  }
  onAddItem: (sectionId: string, data: Record<string, unknown>) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
}

export interface ScopeAgContextMenuState {
  open: boolean
  x: number
  y: number
  rowId: string | null
}

const CLOSED: ScopeAgContextMenuState = {
  open: false,
  x: 0,
  y: 0,
  rowId: null,
}

/**
 * Build a "blank seed payload" mirroring buildPromotePayload but with
 * a caller-supplied sort_order. Used by the Insert above / below /
 * Duplicate actions so every new row goes through the same minimally-
 * valid create payload shape.
 */
export function buildContextSeedPayload(
  configDefaults: UseScopeAgContextMenuArgs['configDefaults'],
  sortOrder: number,
  cloneFrom?: QuoteLineItem,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    description: '',
    quantity: 0,
    unit: configDefaults.defaultUnit ?? 'm2',
    productivity_pmpd: Number(configDefaults.defaultProductivityPmpd ?? 15),
    sort_order: sortOrder,
  }
  if (!cloneFrom) return base
  const src = cloneFrom as unknown as Record<string, unknown>
  const payload: Record<string, unknown> = { ...base }
  for (const key of Object.keys(src)) {
    if (key === 'id' || key === 'sort_order') continue
    payload[key] = src[key]
  }
  payload.sort_order = sortOrder
  return payload
}

export function useScopeAgContextMenu(args: UseScopeAgContextMenuArgs) {
  const {
    sectionId,
    rows,
    isEditable,
    configDefaults,
    onAddItem,
    onDeleteItem,
  } = args
  const [state, setState] = useState<ScopeAgContextMenuState>(CLOSED)

  const close = useCallback(() => setState(CLOSED), [])

  const openAt = useCallback(
    (rowId: string, x: number, y: number) => {
      if (!isEditable) return
      // Pad rows have no identity yet — opening a menu on them and
      // calling Delete/Duplicate would be a no-op. Skip.
      if (isPadRowId(rowId)) return
      setState({ open: true, x, y, rowId })
    },
    [isEditable],
  )

  /**
   * The row the menu is currently anchored to (if any). Resolved
   * fresh from props each call so the action callbacks always see
   * the latest persisted shape.
   */
  const targetRow = useMemo(
    () => (state.rowId ? rows.find((r) => r.id === state.rowId) ?? null : null),
    [state.rowId, rows],
  )

  const handleInsertAbove = useCallback(() => {
    if (!targetRow) return
    const order = (targetRow.sort_order ?? 1) - 1
    onAddItem(sectionId, buildContextSeedPayload(configDefaults, Math.max(order, 0)))
  }, [targetRow, sectionId, configDefaults, onAddItem])

  const handleInsertBelow = useCallback(() => {
    if (!targetRow) return
    const order = (targetRow.sort_order ?? rows.length) + 1
    onAddItem(sectionId, buildContextSeedPayload(configDefaults, order))
  }, [targetRow, rows.length, sectionId, configDefaults, onAddItem])

  const handleDuplicate = useCallback(() => {
    if (!targetRow) return
    const order = (targetRow.sort_order ?? rows.length) + 1
    onAddItem(
      sectionId,
      buildContextSeedPayload(configDefaults, order, targetRow),
    )
  }, [targetRow, rows.length, sectionId, configDefaults, onAddItem])

  const handleDelete = useCallback(() => {
    if (!targetRow) return
    onDeleteItem(sectionId, targetRow.id)
  }, [targetRow, sectionId, onDeleteItem])

  return {
    state,
    close,
    openAt,
    handleInsertAbove,
    handleInsertBelow,
    handleDuplicate,
    handleDelete,
  }
}
